import { h, Component } from 'preact'
import HeaderNavigation from '../mega-menu/index.sync'
import { z } from 'zod'
import { GetServerSideProps } from '../../server-middleware/context'
import getConfig from '../../services/configCommunity'
import { getGroups } from '../../server-middleware/groupRepository'
import { getCategories, prepCategories } from '../../server-middleware/categoryRepository'
import { decodeSsoUrl } from '../widget-navigation-bar/index.sync'
import { UserPermissionContext } from '../../server-middleware/userContext'
import { ModulesContext } from '../../utils/globals'
import { ParentCategoryInfo, WidgetSetting } from '../mega-menu/Types'

function getCategoryId () {
  if (typeof window === 'undefined') return null
  return window.inSidedData.content.category.id
}

function getContentType () {
  if (typeof window === 'undefined') return undefined
  if (window.location.pathname.match(/^\/ideas/)) {
    return ['ideation']
  }
  if (window.location.pathname.match(/^\/product-updates/)) {
    return ['productUpdate']
  }
  return undefined
}

export interface Props {
  isFederatedSalesforceSearch: boolean
  isFederatedFreshdeskSearch: boolean
  isFederatedSkilljarSearch: boolean
  items: Config['items']
  logo: Config['logo']
  groupCount: number
  communityCategories: ParentCategoryInfo[]
  knowledgeBaseCategories: ParentCategoryInfo[]
  communityCustomerTitle: string
  ssoLoginUrl: string
}

export const widgetName = 'header_navigation' as const

export const config = z.object({
  items: z.array(z.object({
    key: z.string(),
    visibility: z.boolean(),
    name: z.string()
  })),
  logo: z.string()
}).default({
  items: [],
  logo: ''
})

type Config = z.infer<typeof config>

export const getServerSideProps: GetServerSideProps<Config, Props> = async (context, config) => {
  return await context.cache(`header-navigation_${JSON.stringify(config)}_${context.userId}`, async () => {
    const list = await prepCategories(context, await getCategories(context))
    const [isFederatedSalesforceSearch] = (await Promise.allSettled([
      getConfig(context, 'salesforceFederatedSearchEnabled')
    ])).map((result) => {
      if (result.status === 'fulfilled') return result.value || false
      return false
    })

    return {
      items: config.items,
      logo: config.logo,
      groupCount: (await getGroups(context)).length,
      communityCategories: list.filter(c => c.type !== 1),
      knowledgeBaseCategories: list.filter(c => c.type === 1),
      isFederatedSalesforceSearch: isFederatedSalesforceSearch,
      isFederatedFreshdeskSearch: context.config.config.params.freshdesk?.enabledFederatedSearch || false,
      isFederatedSkilljarSearch: context.config.config.params.skilljar?.enabledFederatedSearch || false,
      ssoLoginUrl: decodeSsoUrl(context.config.config.params),
      communityCustomerTitle: context.config.title
    }
  })
}

const getDefaultItems = (props: Props, modules: string[]): WidgetSetting[] => {
  let defaultItems: WidgetSetting[] = [{
    key: 'community',
    visibility: true,
    name: 'Community'
  }]

  if (modules.includes('knowledgeBase')) {
    defaultItems = defaultItems.concat({
      key: 'knowledgeBase',
      visibility: true,
      name: 'Knowledge Base'
    })
  }

  if (modules.includes('ideationV2')) {
    defaultItems = defaultItems.concat({
      key: 'ideation',
      visibility: true,
      name: 'Ideas',
      url: '/ideas'
    })
  }

  if (modules.includes('productUpdates')) {
    defaultItems = defaultItems.concat({
      key: 'productUpdates',
      visibility: true,
      name: 'Product Updates',
      url: '/product-updates'
    })
  }

  if (modules.includes('event')) {
    defaultItems = defaultItems.concat({
      key: 'event',
      visibility: true,
      name: 'Events',
      url: '/events'
    })
  }

  if (modules.includes('group') && props.groupCount) {
    defaultItems = defaultItems.concat({
      key: 'group',
      visibility: true,
      name: 'Groups',
      url: '/groups'
    })
  }

  return defaultItems
}

const getItems = (props: Props, modules: string[]) => {
  if (props.items?.length) {
    const groupIdx = props.items.findIndex(item => item.key === 'group')
    if (groupIdx && !(modules.includes('group') && props.groupCount)) {
      const newItems = props.items.concat([])
      newItems.splice(groupIdx, 1)
      return newItems
    }
    return props.items
  }

  return getDefaultItems(props, modules)
}

export default class WidgetHeaderNavigation extends Component<Props> {
  render (props: Props) {
    return (
      <UserPermissionContext.Consumer>
        {(permissions) => {
          return (
            <ModulesContext.Consumer>
              {(modules) => {
                return (
                  <HeaderNavigation
                    items={getItems(props, modules)}
                    groupCount={props.groupCount}
                    communityCategories={props.communityCategories}
                    knowledgeBaseCategories={props.knowledgeBaseCategories}
                    logo={props.logo}
                    ssoLoginUrl={props.ssoLoginUrl}
                    communityCustomerTitle={props.communityCustomerTitle}
                    displaySearchInWidget // TODO SUC-365 will make this to be true by default
                    searchInfo={{
                      isFederatedSalesforceSearch: props.isFederatedSalesforceSearch,
                      isFederatedSkilljarSearch: props.isFederatedSkilljarSearch,
                      isFederatedFreshdeskSearch: props.isFederatedFreshdeskSearch,
                      category: getCategoryId() || undefined,
                      isParentCategory: false, // TODO implement this and isParentCategory when extending this feature to category page
                      searchByContentType: getContentType()
                    }}
                    permissions={{
                      ideation: permissions.permissions.includes('forum.idea.view') || permissions.permissions.includes('forum-idea-all'),
                      productUpdates: permissions.permissions.includes('forum.product.updates.view') || permissions.permissions.includes('forum-product-updates-all')
                    }}
                  />
                )
              }}
            </ModulesContext.Consumer>
          )
        }}
      </UserPermissionContext.Consumer>
    )
  }
}
